/* ------------------------------------------------------------------
 * Mojito OpenAI Ads tracking — browser identity -> cart attribute bridge
 * ------------------------------------------------------------------
 * Loaded site-wide from snippets/mojito-oai-tracking.liquid (rendered
 * in layout/theme.liquid). Additive — remove that render line to
 * disable. Never touches core Palo Alto files.
 *
 * WHAT IT DOES
 *   Copies up to three browser-side identifiers onto the cart as
 *   attributes; Shopify carries them to the Order's note_attributes,
 *   where the orders/paid CAPI worker reads them:
 *
 *   _oai_oppref  ChatGPT ad-click id. PAID clicks land with ?oppref=<id>
 *                on the URL (a 120-char Fernet token, gAAAAAB…, always
 *                alongside an olref we do not capture); organic ChatGPT
 *                traffic carries none. We persist it in localStorage
 *                (30 days) so it survives to a purchase on a later
 *                visit, and fall back to the SDK's own __oppref cookie
 *                when localStorage is empty — two separate failure
 *                domains, so a browser that loses one still attributes.
 *                When the 30-day window lapses we also CLEAR the
 *                attribute from the cart ('' deletes on
 *                /cart/update.js) — merge semantics would otherwise
 *                keep a stale click id on a long-lived cart past the
 *                window.
 *   _oai_bid     The _shopify_y browser id — the guest join key. The
 *                custom pixel hashes this exact value into
 *                external_id_sha256 for GUESTS, and the worker hashes
 *                this attribute into external_ids_sha256, which is what
 *                lets OpenAI join a guest's browser events to their
 *                server purchase event. BYTE PARITY MATTERS: the pixel
 *                reads decodeURIComponent(cookie) with no trim and no
 *                case fold, so this file must write that identical
 *                string (the SDK's external-id normalize is the
 *                identity function — any drift splits the identity).
 *   _oai_obref   OpenAI's __obref cookie (set by the pixel SDK), the
 *                doc-recommended hybrid-matching signal. Passed RAW,
 *                not percent-decoded — the doc says "include it
 *                unchanged", the same lesson as oppref.
 *
 * DESIGN CONSTRAINTS (verified 20AUG2026)
 *   - Partial POSTs to /cart/update.js MERGE attributes (live-tested
 *     on this store: coexists with mojito-bubble's _mjb_sid and all of
 *     Analyzify's azfy_* attributes). Safe to write alone.
 *   - We only POST when a cart cookie already exists: writing an
 *     attribute on a cartless pageview would mint a cart cookie for
 *     every visitor (and bot) and bust Shopify's page cache.
 *   - Write-once state is BELT AND SUSPENDERS: an in-memory flag (this
 *     page's lifetime) plus a localStorage flag (across pages), plus a
 *     3-strike failure budget per state. Without these, a client whose
 *     localStorage.setItem throws (quota, lockdown) or who gets
 *     persistent non-OK responses (WAF challenge, 429) would re-POST
 *     every watcher tick, forever — a self-inflicted storm now that
 *     this runs for every cart session, not just ad clicks.
 *   - Cart activity is observed via the theme's own document-level
 *     CustomEvents (theme:cart:added / update / loaded / open) plus a
 *     slow cookie watcher (safety net for cart mutations that bypass
 *     theme events — upsell apps, GiftPro auto-add). Deliberately NOT
 *     a window.fetch patch: live-tested 20AUG2026 and another app on
 *     this store replaces window.fetch after our defer script runs,
 *     silently evicting the patch.
 *   - Single `_` prefix: hidden from checkout UI but still visible to
 *     Liquid and the Ajax API. Never use double `__` (invisible).
 *   - PDP "Buy it now" bypasses the cart entirely; those orders have
 *     no attributes by construction. The server leg falls back to
 *     Order.customerJourneySummary.lastVisit.landingPage for oppref;
 *     the bid join is simply absent for them.
 * ------------------------------------------------------------------ */
(function () {
  'use strict';

  var LS_KEY = 'mojito:oai:oppref';        // {v: <oppref>, ts: <epoch ms>}
  var LS_FLAG = 'mojito:oai:written';      // {key, token, oppref} last successful write
  var OPPREF_COOKIE = '__oppref';          // the SDK's own click-id cookie (30d)
  var TTL_MS = 30 * 24 * 60 * 60 * 1000;   // 30 days — mirrors __oppref cookie
  var DEBOUNCE_MS = 800;
  var WATCH_MS = 10000;
  var FAIL_MAX = 3;                        // failed POSTs per state per page load

  var CART_EVENTS = [
    'theme:cart:added',   // item added (drawer flow)
    'theme:cart:update',
    'theme:cart:loaded',
    'theme:cart:open'
  ];

  var timer = null;
  var inFlight = false;
  var lastFlag = null;   // in-memory success mirror — survives broken localStorage
  var failKey = null;    // state whose POSTs keep failing
  var fails = 0;

  function root() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function rawCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
      return m ? m[1] : null;
    } catch (e) { return null; }
  }

  function readStore() {
    try {
      var raw = window.localStorage.getItem(LS_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || !obj.v || !obj.ts) return null;
      if (Date.now() - obj.ts > TTL_MS) {
        window.localStorage.removeItem(LS_KEY);
        return null;
      }
      return obj.v;
    } catch (e) { return null; }
  }

  function capture() {
    try {
      var v = new URLSearchParams(window.location.search).get('oppref');
      if (!v) return;
      // Last click wins — a fresh ad click restarts the 30-day window.
      window.localStorage.setItem(LS_KEY, JSON.stringify({ v: v, ts: Date.now() }));
    } catch (e) {}
  }

  /* Second, independent source for the click id: the SDK's own __oppref
   * cookie. Verified against oaiq.min.js v0.1.32 — its on() stores
   * encodeURIComponent(URLSearchParams.get('oppref')) with a 720h Max-Age and
   * reads it back through decodeURIComponent, falling back to the raw string
   * when that throws. Decoding here therefore yields the byte-identical string
   * capture() puts in localStorage, so the two paths cannot disagree.
   *
   * Why it matters: cookies and localStorage are separate failure domains. A
   * browser that blocks or evicts localStorage (private mode, lockdown, ITP
   * eviction) still carries the click id in the cookie, and without this the
   * server leg silently loses an attribution the browser leg still has —
   * producing an order that looks organic but was paid. The SDK clears the
   * cookie by writing '' (Xt()), so treat empty as absent. */
  function cookieOppref() {
    var raw = rawCookie(OPPREF_COOKIE);
    if (!raw) return null;
    try { return decodeURIComponent(raw) || null; } catch (e) { return raw; }
  }

  function cartToken() {
    return rawCookie('cart');
  }

  function readFlag() {
    try {
      var f = JSON.parse(window.localStorage.getItem(LS_FLAG));
      return (f && typeof f === 'object') ? f : null; // legacy string flags -> null
    } catch (e) { return null; }
  }

  /* Everything we currently want on the cart. _oai_bid must be the byte-
   * identical string the pixel hashes: decodeURIComponent of the cookie,
   * nothing else. _oai_obref rides raw ("include it unchanged"). Length
   * caps guard against pathological values bloating the order (the worker
   * rejects over-cap values anyway, so writing them would join nothing). */
  function desired() {
    var out = {};
    // localStorage first (ours, refreshed on every click), then the SDK cookie.
    var oppref = readStore() || cookieOppref();
    if (oppref && String(oppref).length <= 512) out._oai_oppref = oppref;
    var bid = rawCookie('_shopify_y');
    if (bid) {
      try { bid = decodeURIComponent(bid); } catch (e) { bid = null; }
      if (bid && bid.length <= 128) out._oai_bid = bid;
    }
    var obref = rawCookie('__obref');
    if (obref && obref.length <= 512) out._oai_obref = obref;
    return out;
  }

  function keyOf(attrs) {
    return (attrs._oai_oppref || '') + '~' + (attrs._oai_bid || '') + '~' + (attrs._oai_obref || '');
  }

  /* What still needs writing — null when the cart is current, unreachable,
   * or this exact state already burned its failure budget. Shared by the
   * event path and the watcher so both apply identical gates. */
  function pending() {
    var token = cartToken();
    if (!token) return null; // no cart yet — a later cart event will call us back

    var attrs = desired();
    var stored = readFlag();
    // Expired/absent oppref that THIS device previously wrote onto THIS
    // cart: clear it ('' deletes the attribute; merge keeps the rest).
    // Gated on stored.token so another device's fresher write survives.
    if (!attrs._oai_oppref && stored && stored.token === token && stored.oppref) {
      attrs._oai_oppref = '';
    }
    var key = keyOf(attrs);
    if (key === '~~') return null; // nothing to write

    var flagStr = key + '|' + token;
    if (flagStr === lastFlag) return null;                              // done (this page)
    if (stored && stored.key === key && stored.token === token) return null; // done (persisted)
    if (flagStr === failKey && fails >= FAIL_MAX) return null;          // give up on this state
    return { attrs: attrs, key: key, token: token, flagStr: flagStr };
  }

  function noteFailure(flagStr) {
    if (failKey === flagStr) fails++;
    else { failKey = flagStr; fails = 1; }
  }

  function assert() {
    if (inFlight) return;
    var p = pending();
    if (!p) return;

    inFlight = true;
    fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify({ attributes: p.attrs })
    }).then(function (res) {
      inFlight = false;
      if (!res.ok) { noteFailure(p.flagStr); return; }
      lastFlag = p.flagStr;
      failKey = null; fails = 0;
      try {
        window.localStorage.setItem(LS_FLAG, JSON.stringify({
          key: p.key, token: p.token, oppref: p.attrs._oai_oppref || ''
        }));
      } catch (e) {} // lastFlag still stops rewrites for this page's lifetime
    }).catch(function () { inFlight = false; noteFailure(p.flagStr); });
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(assert, DEBOUNCE_MS);
  }

  // --- boot ---
  capture(); // persist ?oppref= before anything else can navigate away

  // Theme cart lifecycle — un-evictable, dispatched on document by theme.js.
  for (var i = 0; i < CART_EVENTS.length; i++) {
    try { document.addEventListener(CART_EVENTS[i], schedule); } catch (e) {}
  }
  // Returning to a restored page (bfcache) — cart may have changed meanwhile.
  try { window.addEventListener('pageshow', schedule); } catch (e) {}

  // _shopify_y exists on effectively every session now, so the bridge is no
  // longer ad-click-only: check on every load, and keep the slow watcher on
  // for everyone. Cost per tick is two cookie reads and a string compare;
  // network writes stay gated by pending() — cart cookie, both write flags,
  // and the failure budget.
  schedule();
  setInterval(function () {
    if (pending()) schedule();
  }, WATCH_MS);
})();
